import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { StaffRail } from '@/components/layout/StaffRail';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ShieldCheck, KeyRound, CheckCircle2, AlertCircle, Save, Smartphone, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
}

export default function MyProfile() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  // PIN form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/staff/me')
      .then((r) => setMe(r.data))
      .catch(() => toast.error('Could not load profile'))
      .finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    if (!/^\d{4,6}$/.test(newPin)) { toast.error('PIN must be 4–6 digits'); return; }
    if (newPin !== confirmPin) { toast.error('PINs do not match'); return; }
    if (!currentPassword) { toast.error('Enter your login password to confirm'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/staff/me/pin', { current_password: currentPassword, new_pin: newPin });
      toast.success('Kiosk PIN updated');
      setMe({ ...(me || {}), has_kiosk_pin: true, kiosk_pin_updated_at: data.updated_at });
      setCurrentPassword(''); setNewPin(''); setConfirmPin('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update PIN');
    } finally { setSaving(false); }
  };

  const STAFF = ['kitchen', 'cashier', 'driver', 'admin', 'owner'];
  const isStaff = STAFF.includes(user?.role);

  return (
    <div className="min-h-screen bg-background">
      <StaffRail />
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <div className="mb-4">
          <h1 className="font-display text-2xl font-semibold">My profile</h1>
          <p className="text-sm text-muted-foreground">Manage your kiosk PIN and account info.</p>
        </div>

        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!loading && me && (
          <div className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Account</CardTitle></CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Name</p><p className="font-medium">{me.full_name || '—'}</p></div>
                  <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Email</p><p className="font-medium">{me.email}</p></div>
                  <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Role</p><p className="font-medium capitalize">{me.role}</p></div>
                </div>
              </CardContent>
            </Card>

            {isStaff && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <KeyRound className="h-4 w-4 text-primary" /> Kiosk PIN
                  </CardTitle>
                  <p className="mt-1 text-xs text-muted-foreground">Your 4–6 digit PIN for clocking in at the entrance kiosk. Stored encrypted — even an admin can&apos;t read it back, only reset it.</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 p-3">
                    {me.has_kiosk_pin ? (
                      <Badge data-testid="profile-pin-set-badge" className="border border-emerald-200 bg-emerald-50 text-emerald-900"><CheckCircle2 className="mr-1 h-3 w-3" /> PIN configured</Badge>
                    ) : (
                      <Badge data-testid="profile-pin-missing-badge" className="border border-amber-200 bg-amber-50 text-amber-900"><AlertCircle className="mr-1 h-3 w-3" /> No PIN set yet</Badge>
                    )}
                    {me.kiosk_pin_updated_at && (
                      <span className="text-xs text-muted-foreground">Last changed: <strong>{fmtDate(me.kiosk_pin_updated_at)}</strong></span>
                    )}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="cur-pwd">Confirm with login password</Label>
                      <Input
                        id="cur-pwd"
                        data-testid="profile-pin-password-input"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        autoComplete="current-password"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="new-pin">New PIN <span className="text-xs font-normal text-muted-foreground">(4–6 digits)</span></Label>
                      <Input
                        id="new-pin"
                        data-testid="profile-pin-new-input"
                        type="password"
                        inputMode="numeric"
                        maxLength={6}
                        value={newPin}
                        onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                      />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2 sm:max-w-[50%]">
                      <Label htmlFor="confirm-pin">Confirm new PIN</Label>
                      <Input
                        id="confirm-pin"
                        data-testid="profile-pin-confirm-input"
                        type="password"
                        inputMode="numeric"
                        maxLength={6}
                        value={confirmPin}
                        onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button data-testid="profile-pin-save-button" onClick={submit} disabled={saving || !currentPassword || !newPin || !confirmPin}>
                      <Save className="mr-1 h-4 w-4" /> {saving ? 'Saving…' : (me.has_kiosk_pin ? 'Change PIN' : 'Set PIN')}
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    <ShieldCheck className="mr-1 inline h-3 w-3" />
                    Forgot your PIN? Ask any manager — they can wipe it from the Staff page and you can set a fresh one here.
                  </p>
                </CardContent>
              </Card>
            )}

            <TwoFactorCard me={me} onChange={(patch) => setMe((m) => ({ ...(m || {}), ...patch }))} />
          </div>
        )}
      </main>
    </div>
  );
}


// =============================================================================
// Two-factor authentication management card
// =============================================================================
// Surfaced ONLY on the staff profile page (per ops policy 2FA is staff-only
// for now). Backed by the /api/auth/2fa/{setup,enable,disable} endpoints.
//   * If MFA is OFF: shows an "Enable" button. The enrollment dialog walks
//     the user through scanning the QR with their authenticator app and
//     confirming a code.
//   * If MFA is ON: shows the enrollment date + a "Disable" button. The
//     disable dialog requires a fresh code so a stolen session can't
//     silently weaken the account.
function TwoFactorCard({ me, onChange }) {
  const enabled = !!me?.mfa_enabled;
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [enrollStep, setEnrollStep] = useState('idle'); // idle | secret | confirming
  const [secret, setSecret] = useState('');
  const [otpUri, setOtpUri] = useState('');
  const [enrollCode, setEnrollCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [busy, setBusy] = useState(false);

  const beginEnroll = async () => {
    setEnrollOpen(true);
    setEnrollStep('idle');
    setBusy(true);
    try {
      const { data } = await api.post('/auth/2fa/setup');
      setSecret(data.secret || '');
      setOtpUri(data.otpauth_uri || '');
      setEnrollStep('secret');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start 2FA setup');
      setEnrollOpen(false);
    } finally { setBusy(false); }
  };

  const finishEnroll = async () => {
    if (!/^\d{6}$/.test(enrollCode)) { toast.error('Enter the 6-digit code from your authenticator'); return; }
    setBusy(true);
    try {
      await api.post('/auth/2fa/enable', { code: enrollCode });
      toast.success('Two-factor authentication enabled');
      onChange({ mfa_enabled: true, mfa_enrolled_at: new Date().toISOString() });
      setEnrollOpen(false);
      setEnrollCode(''); setSecret(''); setOtpUri('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invalid code — make sure your authenticator clock is correct');
    } finally { setBusy(false); }
  };

  const finishDisable = async () => {
    if (!/^\d{6}$/.test(disableCode)) { toast.error('Enter your current 6-digit code'); return; }
    setBusy(true);
    try {
      await api.post('/auth/2fa/disable', { code: disableCode });
      toast.success('Two-factor authentication disabled');
      onChange({ mfa_enabled: false, mfa_enrolled_at: null });
      setDisableOpen(false);
      setDisableCode('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invalid code');
    } finally { setBusy(false); }
  };

  const copySecret = async () => {
    try { await navigator.clipboard.writeText(secret); toast.success('Secret copied'); }
    catch (e) { toast.error('Copy failed'); }
  };

  return (
    <Card data-testid="my-profile-2fa-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Smartphone className="h-4 w-4 text-primary" /> Two-factor authentication
          </CardTitle>
          {enabled ? (
            <Badge data-testid="2fa-status-badge" variant="secondary" className="border-emerald-200 bg-emerald-50 text-emerald-700">
              <CheckCircle2 className="mr-1 h-3 w-3" /> Enabled
            </Badge>
          ) : (
            <Badge data-testid="2fa-status-badge" variant="secondary" className="border-amber-200 bg-amber-50 text-amber-700">
              <AlertCircle className="mr-1 h-3 w-3" /> Not enabled
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Adds a 6-digit code from your phone (Google Authenticator, Authy, 1Password, Microsoft Authenticator) on top of your password. Once enabled, every login on a new device requires the code.
        </p>
        {enabled ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Enrolled {me?.mfa_enrolled_at ? fmtDate(me.mfa_enrolled_at) : 'recently'}.
            </p>
            <Button data-testid="2fa-disable-button" size="sm" variant="outline" onClick={() => setDisableOpen(true)}>
              Disable 2FA
            </Button>
          </div>
        ) : (
          <Button data-testid="2fa-enable-button" size="sm" onClick={beginEnroll}>
            <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Enable 2FA
          </Button>
        )}
      </CardContent>

      {/* Enrollment dialog */}
      <Dialog open={enrollOpen} onOpenChange={(o) => { setEnrollOpen(o); if (!o) { setEnrollStep('idle'); setSecret(''); setOtpUri(''); setEnrollCode(''); } }}>
        <DialogContent data-testid="2fa-enroll-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enable two-factor authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app, then enter the 6-digit code it shows.
            </DialogDescription>
          </DialogHeader>
          {busy && enrollStep === 'idle' ? (
            <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : enrollStep === 'secret' ? (
            <div className="space-y-4">
              <div className="flex justify-center rounded-lg border bg-white p-3">
                {otpUri ? (
                  <QRCodeCanvas data-testid="2fa-qr-canvas" value={otpUri} size={184} includeMargin={false} />
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Can&apos;t scan? Enter this code manually in your app:</Label>
                <div className="flex items-center gap-2">
                  <Input data-testid="2fa-secret-display" value={secret} readOnly className="font-mono text-xs" />
                  <Button data-testid="2fa-copy-secret" variant="outline" size="icon" onClick={copySecret}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="2fa-enroll-code">6-digit code from your app</Label>
                <Input
                  id="2fa-enroll-code"
                  data-testid="2fa-enroll-code-input"
                  inputMode="numeric"
                  maxLength={6}
                  value={enrollCode}
                  onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="font-mono tracking-widest"
                  autoComplete="one-time-code"
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button data-testid="2fa-enroll-cancel" variant="ghost" onClick={() => setEnrollOpen(false)} disabled={busy}>Cancel</Button>
            <Button data-testid="2fa-enroll-confirm" onClick={finishEnroll} disabled={busy || enrollCode.length !== 6}>
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />}
              Enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable dialog */}
      <Dialog open={disableOpen} onOpenChange={(o) => { setDisableOpen(o); if (!o) setDisableCode(''); }}>
        <DialogContent data-testid="2fa-disable-dialog">
          <DialogHeader>
            <DialogTitle>Disable two-factor authentication</DialogTitle>
            <DialogDescription>
              Enter your current 6-digit code to confirm. After disabling, only your password will be required to log in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="2fa-disable-code">Current code</Label>
            <Input
              id="2fa-disable-code"
              data-testid="2fa-disable-code-input"
              inputMode="numeric"
              maxLength={6}
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              className="font-mono tracking-widest"
              autoComplete="one-time-code"
            />
          </div>
          <DialogFooter>
            <Button data-testid="2fa-disable-cancel" variant="ghost" onClick={() => setDisableOpen(false)} disabled={busy}>Cancel</Button>
            <Button data-testid="2fa-disable-confirm" variant="destructive" onClick={finishDisable} disabled={busy || disableCode.length !== 6}>
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Disable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
